export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

type RequestOptions = {
  method?: string;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  token: string,
  options: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, params } = options;

  let url = `${API_BASE}${path}`;
  if (params) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) qs.set(k, String(v));
    }
    if (qs.toString()) url += `?${qs}`;
  }

  const resp = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!resp.ok) {
    const err = (await resp.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
    throw new ApiError(resp.status, err.error ?? 'Request failed');
  }

  return (await resp.json()) as T;
}

// ── Agencies ────────────────────────────────────────────────────────────────

export interface AgencyMember {
  id: string;
  agency_id: string;
  user_id: string;
  role: string;
  /** 1 once the member has been limited to specific brands. */
  workspace_scoped?: number;
  name: string | null;
  email: string;
  created_at: string;
}

export const agenciesApi = {
  list: (token: string) => request<{ agencies: Agency[] }>('/api/agencies', token),
  get: (token: string, id: string) =>
    request<{ agency: Agency }>(`/api/agencies/${id}`, token),
  create: (token: string, name: string) =>
    request<{ agency: Agency }>('/api/agencies', token, { method: 'POST', body: { name } }),
  update: (token: string, id: string, data: Partial<Agency>) =>
    request<{ agency: Agency }>(`/api/agencies/${id}`, token, { method: 'PATCH', body: data }),
  delete: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/agencies/${id}`, token, { method: 'DELETE' }),
  listMembers: (token: string, agencyId: string) =>
    request<{ members: AgencyMember[] }>(`/api/agencies/${agencyId}/members`, token),
  removeMember: (token: string, agencyId: string, memberId: string) =>
    request<{ success: boolean }>(`/api/agencies/${agencyId}/members/${memberId}`, token, { method: 'DELETE' }),
  updateMemberRole: (token: string, agencyId: string, memberId: string, role: string) =>
    request<{ success: boolean }>(`/api/agencies/${agencyId}/members/${memberId}`, token, {
      method: 'PATCH',
      body: { role },
    }),
};

// ── Workspace member assignment ──────────────────────────────────────────────

export interface WorkspaceMember {
  id: string;
  user_id: string;
  role: 'editor' | 'viewer';
  email: string;
  name: string | null;
  created_at: string;
}

export const workspaceMembersApi = {
  list: (token: string, workspaceId: string) =>
    request<{ members: WorkspaceMember[] }>(`/api/workspaces/${workspaceId}/members`, token),
  assign: (token: string, workspaceId: string, userId: string, role: 'editor' | 'viewer') =>
    request<{ success: boolean }>(`/api/workspaces/${workspaceId}/members`, token, {
      method: 'PUT',
      body: { user_id: userId, role },
    }),
  unassign: (token: string, workspaceId: string, userId: string) =>
    request<{ success: boolean }>(`/api/workspaces/${workspaceId}/members/${userId}`, token, {
      method: 'DELETE',
    }),
};

// ── Workspaces ──────────────────────────────────────────────────────────────

export const workspacesApi = {
  list: (token: string, agencyId: string) =>
    request<{ workspaces: Workspace[] }>('/api/workspaces', token, {
      params: { agencyId },
    }),
  get: (token: string, id: string) =>
    request<{ workspace: Workspace; role?: string }>(`/api/workspaces/${id}`, token),
  create: (token: string, data: CreateWorkspaceInput) =>
    request<{ workspace: Workspace }>('/api/workspaces', token, { method: 'POST', body: data }),
  update: (token: string, id: string, data: WorkspaceUpdate) =>
    request<{ workspace: Workspace }>(`/api/workspaces/${id}`, token, {
      method: 'PATCH',
      body: data,
    }),
  delete: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/workspaces/${id}`, token, { method: 'DELETE' }),
};

// ── Social Accounts ─────────────────────────────────────────────────────────

export const socialAccountsApi = {
  list: (token: string, workspaceId: string) =>
    request<{ accounts: SocialAccount[] }>('/api/social-accounts', token, {
      params: { workspaceId },
    }),
  delete: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/social-accounts/${id}`, token, { method: 'DELETE' }),
  getOAuthUrl: (token: string, workspaceId: string, platform: string) =>
    request<{ authUrl: string }>('/api/social-accounts/oauth/init', token, {
      params: { workspaceId, platform },
    }),
  getPendingAccounts: (token: string, pendingId: string) =>
    request<{ platform: string; workspaceId: string; accounts: PendingAccount[] }>(
      `/api/social-accounts/oauth/pending/${pendingId}`,
      token
    ),
  finalizePending: (token: string, pendingId: string, accountIds: string[]) =>
    request<{ success: boolean; count: number }>(
      `/api/social-accounts/oauth/pending/${pendingId}/finalize`,
      token,
      { method: 'POST', body: { accountIds } }
    ),
};

// ── Posts ────────────────────────────────────────────────────────────────────

export const postsApi = {
  list: (token: string, params: PostListParams) =>
    request<{ posts: Post[]; total: number }>('/api/posts', token, { params: { ...params } }),
  stats: (token: string, workspaceId: string) =>
    request<{ total: number; byStatus: Record<string, number> }>('/api/posts/stats', token, {
      params: { workspaceId },
    }),
  get: (token: string, id: string) =>
    request<{ post: Post; results: PostResult[] }>(`/api/posts/${id}`, token),
  create: (token: string, data: CreatePostInput) =>
    request<{ post: Post; enqueued?: boolean; enqueue_error?: string; approval_required?: boolean }>(
      '/api/posts',
      token,
      { method: 'POST', body: data }
    ),
  update: (token: string, id: string, data: UpdatePostInput) =>
    request<{ post: Post; approval_required?: boolean }>(`/api/posts/${id}`, token, {
      method: 'PATCH',
      body: data,
    }),
  /** Re-publish only to the accounts whose latest result failed (or is missing). */
  retry: (token: string, id: string) =>
    request<{ post: Post; retrying: string[]; approval_required?: boolean }>(
      `/api/posts/${id}/retry`,
      token,
      { method: 'POST', body: {} }
    ),
  delete: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/posts/${id}`, token, { method: 'DELETE' }),
  duplicate: (token: string, id: string, data?: { queue_id?: string; scheduled_at?: string }) =>
    request<{ post: Post; enqueued?: boolean; enqueue_error?: string; scheduled_at?: string }>(
      `/api/posts/${id}/duplicate`,
      token,
      { method: 'POST', body: data ?? {} }
    ),
  bulk: (token: string, data: BulkPostAction) =>
    request<{ success: boolean }>('/api/posts/bulk', token, { method: 'POST', body: data }),
};

// (inboxApi defined below with extended methods)

// ── AI ───────────────────────────────────────────────────────────────────────

export const aiApi = {
  generate: (token: string, data: AIGenerateRequest) =>
    request<{ result: string; variants?: Array<{ platform: string; content: string }> }>(
      '/api/ai/generate',
      token,
      { method: 'POST', body: data }
    ),
  generateImage: (token: string, data: AIImageRequest) =>
    request<{ mediaId: string; r2Key: string }>('/api/ai/generate-image', token, {
      method: 'POST',
      body: data,
    }),
  generateCaption: (
    token: string,
    data: { workspaceId: string; mediaKey: string; platform?: string; hint?: string }
  ) =>
    request<{ caption: string; mode: 'vision' | 'text' | 'video-text' }>(
      '/api/ai/generate-caption',
      token,
      { method: 'POST', body: data }
    ),
  models: (token: string) =>
    request<{
      text: { cloudflare: AIModel[]; xai: AIModel[] };
      image: { cloudflare: AIModel[]; xai: AIModel[] };
    }>('/api/ai/models', token),
};

// ── Scheduling Queues ────────────────────────────────────────────────────────

export type SlotContentType = 'image' | 'video' | 'text' | 'any';

export interface QueueSlot {
  id: string;
  queue_id: string;
  day_of_week: number;
  time_minutes: number;
  content_types: SlotContentType[];
  created_at: string;
}

export interface PostQueue {
  id: string;
  workspace_id: string;
  name: string;
  timezone: string;
  paused: number;
  created_at: string;
  slots?: QueueSlot[];
}

export interface CreateQueueInput {
  workspace_id: string;
  name: string;
  timezone?: string;
  slots?: Array<{ day_of_week: number; time_minutes: number; content_types?: SlotContentType[] }>;
}

export const queuesApi = {
  list: (token: string, workspaceId: string) =>
    request<{ queues: PostQueue[] }>('/api/queues', token, { params: { workspaceId } }),
  create: (token: string, data: CreateQueueInput) =>
    request<{ queue: PostQueue }>('/api/queues', token, { method: 'POST', body: data }),
  update: (token: string, id: string, data: { name?: string; timezone?: string; paused?: boolean }) =>
    request<{ queue: PostQueue }>(`/api/queues/${id}`, token, { method: 'PATCH', body: data }),
  delete: (token: string, id: string) =>
    request<{ success: boolean; posts_unscheduled: number }>(`/api/queues/${id}`, token, {
      method: 'DELETE',
    }),
  replaceSlots: (
    token: string,
    id: string,
    slots: Array<{ day_of_week: number; time_minutes: number; content_types?: SlotContentType[] }>
  ) =>
    request<{ slots: QueueSlot[] }>(`/api/queues/${id}/slots`, token, {
      method: 'PUT',
      body: { slots },
    }),
  enqueue: (token: string, id: string, postId: string) =>
    request<{ post: Post; scheduled_at: string; slot_id: string }>(
      `/api/queues/${id}/enqueue`,
      token,
      { method: 'POST', body: { postId } }
    ),
  getPosts: (token: string, id: string) =>
    request<{ posts: Post[] }>(`/api/queues/${id}/posts`, token),
};

// ── Recommendations (X-algorithm-style For You for content planning) ────────

export interface RecommendationCandidate {
  id: string;
  kind: 'content' | 'topic' | 'hashtag' | 'time_slot' | 'platform_mix';
  source: 'in_network' | 'out_of_network';
  text?: string | null;
  reason?: string | null;
  content_hash?: string;
  topic?: string | null;
  hashtags?: string[];
  platforms?: string[];
  scheduled_at?: string | null;
  score?: number;
  weighted_score?: number;
  action_scores?: Record<string, number>;
}

export interface RecommendationsResponse {
  candidates: RecommendationCandidate[];
  meta: {
    sourceCounts: Record<string, number>;
    filteredCounts: Record<string, number>;
    totalConsidered: number;
    selectedCount: number;
  };
}

export const recommendationsApi = {
  forYou: (
    token: string,
    body: {
      workspaceId: string;
      limit?: number;
      platforms?: string[];
      topicSeeds?: string[];
      hashtagSeeds?: string[];
      previouslyServedIds?: string[];
      mutedKeywords?: string[];
      blockedAuthorIds?: string[];
    }
  ) =>
    request<RecommendationsResponse>('/api/recommendations', token, {
      method: 'POST',
      body,
    }),
  feedback: (
    token: string,
    body: {
      workspaceId: string;
      candidateId: string;
      contentHash?: string;
      event: 'used' | 'dismissed';
    }
  ) =>
    request<{ success: boolean }>('/api/recommendations/feedback', token, {
      method: 'POST',
      body,
    }),
  explain: (token: string) =>
    request<{
      pipeline: string[];
      weights: Record<string, number>;
      sources: Record<string, string>;
    }>('/api/recommendations/explain', token),
};

// ── Trends (ScrapeCreators) ──────────────────────────────────────────────────

export type TrendPlatform =
  | 'x'
  | 'instagram'
  | 'facebook'
  | 'linkedin'
  | 'tiktok'
  | 'youtube'
  | 'rumble'
  | 'spotify';

export interface TrendPlatformSupport {
  platform: TrendPlatform;
  label: string;
  discovery: Array<'native' | 'search' | 'competitor'>;
  future?: boolean;
}

export interface TrendConfig {
  workspace_id: string;
  enabled: boolean;
  platforms: string[];
  region: string;
  keyword_seeds: string[];
  hashtag_seeds: string[];
  competitor_handles: Record<string, string[]>;
  inject_into_ai: boolean;
  last_refreshed_at: string | null;
  /** Most recent harvest failure (e.g. exhausted credits); null after a clean run. */
  last_error?: string | null;
  last_error_at?: string | null;
}

export interface TrendSignal {
  id: string;
  workspace_id: string;
  platform: string;
  signal_type: string;
  external_id: string | null;
  title: string | null;
  description: string | null;
  url: string | null;
  author: string | null;
  thumbnail_url: string | null;
  metric_views: number;
  metric_likes: number;
  metric_comments: number;
  metric_shares: number;
  score: number;
  velocity: number | null;
  region: string | null;
  week_of: string;
  fetched_at: string;
}

export interface TrendRecommendation {
  id: string;
  workspace_id: string;
  week_of: string;
  platform: string | null;
  title: string;
  idea: string;
  rationale: string | null;
  suggested_hashtags: string | null;
  source_signal_ids: string | null;
  score: number;
  status: 'new' | 'saved' | 'dismissed' | 'used';
  created_at: string;
}

export const trendsApi = {
  platforms: (token: string) =>
    request<{ supported: TrendPlatformSupport[]; active: string[]; configured: boolean }>(
      '/api/trends/platforms',
      token
    ),
  getConfig: (token: string, workspaceId: string) =>
    request<{ config: TrendConfig; configured: boolean }>('/api/trends/config', token, {
      params: { workspaceId },
    }),
  updateConfig: (
    token: string,
    workspaceId: string,
    patch: Partial<
      Omit<TrendConfig, 'workspace_id' | 'last_refreshed_at' | 'last_error' | 'last_error_at'>
    >
  ) =>
    request<{ config: TrendConfig }>('/api/trends/config', token, {
      method: 'PATCH',
      body: { workspaceId, ...patch },
    }),
  signals: (token: string, workspaceId: string, params: { platform?: string; limit?: number; week?: string } = {}) =>
    request<{ signals: TrendSignal[] }>('/api/trends/signals', token, {
      params: { workspaceId, ...params },
    }),
  refresh: (token: string, workspaceId: string, generateIdeas = true) =>
    // queued=false + alreadyQueued: a harvest started minutes ago; no new one is billed.
    request<{ queued: boolean; alreadyQueued?: boolean; message?: string }>('/api/trends/refresh', token, {
      method: 'POST',
      body: { workspaceId, generateIdeas },
    }),
  recommendations: (token: string, workspaceId: string, week?: string) =>
    request<{ recommendations: TrendRecommendation[] }>('/api/trends/recommendations', token, {
      params: { workspaceId, week },
    }),
  generateRecommendations: (token: string, workspaceId: string) =>
    request<{ recommendations: TrendRecommendation[] }>(
      '/api/trends/recommendations/generate',
      token,
      { method: 'POST', body: { workspaceId } }
    ),
  setRecommendationStatus: (
    token: string,
    id: string,
    status: 'new' | 'saved' | 'dismissed' | 'used'
  ) =>
    request<{ success: boolean }>(`/api/trends/recommendations/${id}`, token, {
      method: 'PATCH',
      body: { status },
    }),
};

// ── Media ────────────────────────────────────────────────────────────────────

// Mirrors the API's upload allow-list and size cap (apps/api/src/routes/media.ts).
// Uploads are proxied through the Worker (100 MiB body limit), so anything
// bigger is rejected here with a clear message instead of a CORS-less edge 413.
export const MEDIA_UPLOAD_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'video/mp4', 'video/quicktime', 'video/webm',
];
export const MEDIA_ACCEPT = MEDIA_UPLOAD_TYPES.join(',');
export const MEDIA_MAX_UPLOAD_BYTES = 95 * 1024 * 1024;

export const mediaApi = {
  list: (token: string, params: MediaListParams) =>
    request<{ media: MediaItem[] }>('/api/media', token, {
      params: { ...params },
    }),
  upload: async (token: string, workspaceId: string, file: File): Promise<{ media: MediaItem }> => {
    if (!MEDIA_UPLOAD_TYPES.includes(file.type)) {
      throw new ApiError(415, 'Unsupported file type. Allowed: JPEG, PNG, GIF, WebP, MP4, MOV, WebM');
    }
    if (file.size > MEDIA_MAX_UPLOAD_BYTES) {
      throw new ApiError(
        413,
        `File is too large (${Math.ceil(file.size / 1024 / 1024)} MB). The upload limit is ${MEDIA_MAX_UPLOAD_BYTES / 1024 / 1024} MB`
      );
    }
    const form = new FormData();
    form.append('workspaceId', workspaceId);
    form.append('file', file);
    let resp: Response;
    try {
      resp = await fetch(`${API_BASE}/api/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
    } catch {
      // An edge rejection (e.g. 413) has no CORS headers and surfaces as a TypeError.
      throw new ApiError(0, 'The file may be too large, or the network connection dropped');
    }
    if (!resp.ok) {
      const err = (await resp.json().catch(() => ({ error: 'Unknown error' }))) as { error?: string };
      throw new ApiError(resp.status, err.error ?? 'Upload failed');
    }
    return (await resp.json()) as { media: MediaItem };
  },
  getUploadUrl: (token: string, data: UploadUrlRequest) =>
    request<{ uploadId: string; r2Key: string }>('/api/media/upload-url', token, {
      method: 'POST',
      body: data,
    }),
  confirm: (token: string, data: ConfirmUploadRequest) =>
    request<{ media: MediaItem }>('/api/media/confirm', token, { method: 'POST', body: data }),
  // Returns 409 when draft/scheduled posts still use the media; pass force to delete anyway.
  delete: (token: string, id: string, force = false) =>
    request<{ success: boolean }>(`/api/media/${id}`, token, {
      method: 'DELETE',
      params: force ? { force: 1 } : undefined,
    }),
  // Public URL so <img>/<video> tags (which can't send the auth header) load.
  serveUrl: (r2Key: string) => `${API_BASE}/api/public-media/${r2Key}`,
};

// ── Analytics ────────────────────────────────────────────────────────────────

export interface MonthlyReport {
  month: string;
  period: { since: string; until: string };
  totals: { impressions: number; engagements: number; likes: number; comments: number; shares: number };
  platforms: Array<{
    platform: string; impressions: number; engagements: number;
    likes: number; comments: number; shares: number; posts: number;
  }>;
  followerGrowth: Array<{ platform: string; start: number; end: number; delta: number }>;
  topPosts: AnalyticsTopPost[];
  recap: Array<{
    id: string; content: string; published_at: string | null; platforms: string;
    media_keys: string | null; success_count: number; failed_count: number;
    impressions: number; engagements: number;
  }>;
  upcoming: Array<{ id: string; content: string; scheduled_at: string; platforms: string }>;
  publishedCount: number;
}

export const analyticsApi = {
  summary: (token: string, workspaceId: string, days = 30) =>
    request<AnalyticsSummary>('/api/analytics/summary', token, { params: { workspaceId, days } }),
  accounts: (token: string, workspaceId: string) =>
    request<{ accounts: unknown[] }>('/api/analytics/accounts', token, { params: { workspaceId } }),
  topPosts: (token: string, workspaceId: string, limit = 10, days?: number) =>
    request<{ posts: AnalyticsTopPost[] }>('/api/analytics/top-posts', token, {
      params: days ? { workspaceId, limit, days } : { workspaceId, limit },
    }),
  report: (token: string, workspaceId: string, month?: string) =>
    request<{ report: MonthlyReport }>('/api/analytics/report', token, {
      params: month ? { workspaceId, month } : { workspaceId },
    }),
  reportNarrative: (token: string, workspaceId: string, month: string) =>
    request<{ narrative: string }>('/api/analytics/report/narrative', token, {
      method: 'POST',
      body: { workspaceId, month },
    }),
  shareReport: (token: string, workspaceId: string, month: string, narrative?: string) =>
    request<{ share: { id: string; token: string; month: string; expires_at: string; url: string } }>(
      '/api/analytics/report/share',
      token,
      { method: 'POST', body: { workspaceId, month, narrative } }
    ),
  revokeReportShares: (token: string, workspaceId: string, month: string) =>
    request<{ success: boolean }>('/api/analytics/report/share', token, {
      method: 'DELETE',
      params: { workspaceId, month },
    }),
  refresh: (token: string, workspaceId: string) =>
    request<{ refreshed: number; posts: number; externalPosts: number; errors: string[] }>(
      '/api/analytics/refresh',
      token,
      { method: 'POST', body: { workspaceId } }
    ),
};

// ── Morning Brief ────────────────────────────────────────────────────────────

export interface BriefItem {
  type:
    | 'schedule_gap'
    | 'inbox_backlog'
    | 'recycle_winner'
    | 'trend_idea'
    | 'publish_failures'
    | 'follower_move'
    | 'all_clear';
  title: string;
  body: string;
  link: string;
  postId?: string;
  recommendationId?: string;
}

export const briefApi = {
  get: (token: string, workspaceId: string) =>
    request<{ brief: { brief_date: string; items: BriefItem[] } }>('/api/brief', token, {
      params: { workspaceId },
    }),
  generate: (token: string, workspaceId: string) =>
    request<{ brief: { brief_date: string; items: BriefItem[] } }>('/api/brief/generate', token, {
      method: 'POST',
      body: { workspaceId },
    }),
};

// ── Content Capture ──────────────────────────────────────────────────────────

export interface CaptureResult {
  platform: 'x' | 'instagram' | 'facebook' | 'tiktok';
  caption: string;
  author: string | null;
  sourceUrl: string;
  media: Array<{
    id: string;
    r2_key: string;
    filename: string;
    mime_type: string;
    size_bytes: number;
    is_video: boolean;
  }>;
}

export const captureApi = {
  capture: (token: string, workspaceId: string, url: string) =>
    request<{ capture: CaptureResult }>('/api/capture', token, {
      method: 'POST',
      body: { workspaceId, url },
    }),
};

// ── Notifications ─────────────────────────────────────────────────────────────

export const notificationsApi = {
  list: (token: string, agencyId: string, unreadOnly = false) =>
    request<{ notifications: Notification[] }>('/api/notifications', token, {
      params: { agencyId, unreadOnly },
    }),
  unreadCount: (token: string, agencyId: string) =>
    request<{ count: number }>('/api/notifications/unread-count', token, { params: { agencyId } }),
  markRead: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/notifications/${id}/read`, token, { method: 'PATCH' }),
  markAllRead: (token: string, agencyId: string) =>
    request<{ success: boolean }>('/api/notifications/mark-all-read', token, {
      method: 'POST',
      body: { agencyId },
    }),
};

// ── Invitations ───────────────────────────────────────────────────────────────

export const invitationsApi = {
  list: (token: string, agencyId: string) =>
    request<{ invitations: Invitation[] }>('/api/invitations', token, { params: { agencyId } }),
  create: (token: string, data: { agencyId: string; email: string; role: string }) =>
    request<{ invitation: Invitation; agencyName: string; inviteUrl: string; emailed?: boolean; emailError?: string }>(
      '/api/invitations',
      token,
      { method: 'POST', body: data }
    ),
  revoke: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/invitations/${id}`, token, { method: 'DELETE' }),
  // Public: works signed out, so the invite page can show who's inviting first.
  lookup: (token: string | null, inviteToken: string) =>
    request<{ invitation: InvitationLookup }>(`/api/invitations/lookup/${encodeURIComponent(inviteToken)}`, token ?? ''),
  accept: (token: string, inviteToken: string) =>
    request<{ success: boolean; agencyId: string; role: string }>('/api/invitations/accept', token, {
      method: 'POST',
      body: { token: inviteToken },
    }),
};

// ── Approvals ─────────────────────────────────────────────────────────────────

export const approvalsApi = {
  listPending: (token: string, workspaceId: string) =>
    request<{ posts: Post[] }>('/api/approvals', token, { params: { workspaceId } }),
  submit: (token: string, postId: string) =>
    request<{ success: boolean }>(`/api/approvals/${postId}/submit`, token, { method: 'POST', body: {} }),
  approve: (token: string, postId: string, note?: string) =>
    request<{ success: boolean; newStatus: string }>(`/api/approvals/${postId}/approve`, token, {
      method: 'POST',
      body: { note },
    }),
  reject: (token: string, postId: string, note?: string) =>
    request<{ success: boolean }>(`/api/approvals/${postId}/reject`, token, {
      method: 'POST',
      body: { note },
    }),
};

// ── Inbox (extended) ──────────────────────────────────────────────────────────

export const inboxApi = {
  list: (token: string, params: InboxListParams) =>
    request<{ items: InboxItem[] }>('/api/inbox', token, {
      params: { ...params },
    }),
  get: (token: string, id: string) =>
    request<{ item: InboxItem; replies: InboxReply[] }>(`/api/inbox/${id}`, token),
  reply: (token: string, id: string, content: string) =>
    request<{ success: boolean; replyId: string; platformReplyId?: string }>(
      `/api/inbox/${id}/reply`,
      token,
      { method: 'POST', body: { content } }
    ),
  markRead: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/inbox/${id}/read`, token, { method: 'PATCH' }),
  markAllRead: (token: string, workspaceId: string) =>
    request<{ success: boolean }>('/api/inbox/mark-all-read', token, {
      method: 'POST',
      body: { workspaceId },
    }),
  sync: (token: string, workspaceId: string) =>
    request<{ success: boolean; accounts: number; added: number }>('/api/inbox/sync', token, {
      method: 'POST',
      body: { workspaceId },
    }),
  unreadCount: (token: string, workspaceId: string) =>
    request<{ count: number }>('/api/inbox/unread-count', token, {
      params: { workspaceId },
    }),
};

// ── Canva ────────────────────────────────────────────────────────────────────

export const canvaApi = {
  status: (token: string, agencyId: string) =>
    request<{ connected: boolean; connection: unknown }>('/api/canva/status', token, {
      params: { agencyId },
    }),
  getOAuthUrl: (token: string, agencyId: string) =>
    request<{ authUrl: string }>('/api/canva/oauth/init', token, { params: { agencyId } }),
  designs: (token: string, agencyId: string) =>
    request<{ items: CanvaDesign[] }>('/api/canva/designs', token, { params: { agencyId } }),
  export: (token: string, data: CanvaExportRequest) =>
    request<{ mediaId: string; r2Key: string }>('/api/canva/export', token, {
      method: 'POST',
      body: data,
    }),
  disconnect: (token: string, agencyId: string) =>
    request<{ success: boolean }>('/api/canva/disconnect', token, {
      method: 'DELETE',
      body: { agencyId },
    }),
};

// ── ScrapeCreators ─────────────────────────────────────────────────────────────

export type ScrapeCreatorsPlatform = 'tiktok' | 'instagram' | 'youtube' | 'x';

export const scrapeCreatorsApi = {
  // Lookups spend shared credits, so each is scoped to a workspace (editor+).
  profile: (
    token: string,
    workspaceId: string,
    platform: ScrapeCreatorsPlatform,
    handle: string
  ) =>
    request<{ data: unknown }>('/api/scrapecreators/profile', token, {
      params: { workspaceId, platform, handle },
    }),
  posts: (
    token: string,
    workspaceId: string,
    platform: ScrapeCreatorsPlatform,
    handle: string,
    opts: { cursor?: string; trim?: boolean } = {}
  ) =>
    request<{ data: unknown }>('/api/scrapecreators/posts', token, {
      params: { workspaceId, platform, handle, cursor: opts.cursor, trim: opts.trim },
    }),
  transcript: (
    token: string,
    workspaceId: string,
    platform: ScrapeCreatorsPlatform,
    url: string,
    language?: string
  ) =>
    request<{ data: unknown }>('/api/scrapecreators/transcript', token, {
      params: { workspaceId, platform, url, language },
    }),
  enrichAnalytics: (token: string, workspaceId: string) =>
    request<ScrapeCreatorsEnrichResult>('/api/scrapecreators/enrich-analytics', token, {
      method: 'POST',
      body: { workspaceId },
    }),
};

export interface ScrapeCreatorsEnrichResult {
  enriched: number;
  skipped: number;
  total: number;
  errors: Array<{ account: string; error: string }>;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface Agency {
  id: string;
  name: string;
  owner_user_id: string;
  ai_model: string;
  xai_api_key: string | null;
  created_at: string;
  role?: string;
}

export interface Workspace {
  id: string;
  agency_id: string;
  name: string;
  logo_r2_key: string | null;
  brand_colors: string | null;
  brand_voice: string | null;
  tone: string | null;
  target_audience: string | null;
  website: string | null;
  services: string | null;
  ai_caption_guidance: string | null;
  ai_hashtag_strategy: string | null;
  ai_model_override: string | null;
  xai_api_key_override: string | null;
  require_approval?: number;
  created_at: string;
}

/** Fields the workspace PATCH accepts. Colors and services are arrays; the API stores JSON. */
export interface WorkspaceUpdate {
  name?: string;
  logo_r2_key?: string | null;
  brand_colors?: string[] | null;
  brand_voice?: string | null;
  tone?: string | null;
  target_audience?: string | null;
  website?: string | null;
  services?: string[] | null;
  ai_caption_guidance?: string | null;
  ai_hashtag_strategy?: string | null;
  ai_model_override?: string | null;
  xai_api_key_override?: string | null;
  require_approval?: boolean;
}

export interface CreateWorkspaceInput {
  agency_id: string;
  name: string;
  brand_colors?: string[];
  brand_voice?: string;
  tone?: string;
  target_audience?: string;
  website?: string;
  services?: string[];
}

export interface WorkspaceAIOverride {
  ai_model_override?: string | null;
  xai_api_key_override?: string | null;
}

export interface SocialAccount {
  id: string;
  workspace_id: string;
  platform: string;
  account_name: string;
  account_id: string;
  token_expires_at: string | null;
  profile_image_url: string | null;
  created_at: string;
}

// A candidate account awaiting the user's selection after OAuth (Meta platforms
// can return several Pages / Business accounts from a single grant).
export interface PendingAccount {
  accountId: string;
  accountName: string;
  profileImageUrl: string | null;
}

export interface Post {
  id: string;
  workspace_id: string;
  content: string;
  media_keys: string | null;
  platforms: string;
  status: string;
  scheduled_at: string | null;
  published_at: string | null;
  recurring_rule: string | null;
  per_platform_overrides: string | null;
  labels: string | null;
  created_by: string;
  created_at: string;
  approval_status: string | null;
  approval_note: string | null;
  is_evergreen?: number;
  recycle_interval_days?: number;
  max_recycles?: number;
  recycle_count?: number;
  source_post_id?: string | null;
  /** Latest publish outcome per target account (list + detail endpoints). */
  results?: PostResult[];
}

export interface PostResult {
  social_account_id: string;
  /** Null when the account was disconnected before any snapshot existed. */
  platform: string | null;
  account_name: string | null;
  platform_post_id: string | null;
  status: 'success' | 'failed';
  error_message: string | null;
  published_at: string | null;
}

export interface YouTubePostOptions {
  privacy?: 'public' | 'unlisted' | 'private';
  tags?: string[];
  categoryId?: string;
  madeForKids?: boolean;
  thumbnailKey?: string;
}

export interface CreatePostInput {
  workspace_id: string;
  content: string;
  platforms: string[];
  media_keys?: string[];
  scheduled_at?: string;
  status?: string;
  recurring_rule?: string;
  per_platform_overrides?: Record<
    string,
    { content?: string; media_keys?: string[]; youtube?: YouTubePostOptions }
  >;
  labels?: string[];
  queue_id?: string;
}

export interface UpdatePostInput
  extends Partial<Omit<CreatePostInput, 'workspace_id' | 'queue_id' | 'scheduled_at'>> {
  /** null clears the schedule (with status 'draft' it unschedules the post). */
  scheduled_at?: string | null;
  is_evergreen?: boolean;
}

export interface PostListParams {
  workspaceId: string;
  status?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
  /** Server-side content search. */
  q?: string;
  /** Default 'created_desc'; 'scheduled_asc' = soonest scheduled first. */
  sort?: 'created_desc' | 'scheduled_asc';
}

export interface BulkPostAction {
  postIds: string[];
  action: 'delete' | 'reschedule';
  scheduled_at?: string;
}

export interface InboxItem {
  id: string;
  workspace_id: string;
  social_account_id: string;
  platform: string;
  type: string;
  author_name: string | null;
  author_avatar: string | null;
  content: string | null;
  platform_item_id: string;
  read_at: string | null;
  created_at: string;
}

export interface InboxListParams {
  workspaceId: string;
  platform?: string;
  type?: string;
  read?: string;
  limit?: number;
  offset?: number;
}

export interface AIGenerateRequest {
  workspaceId: string;
  prompt: string;
  type: string;
  platform?: string;
  /** One generation that returns a variant per platform. */
  platforms?: string[];
  tone?: string;
  length?: string;
  useXai?: boolean;
  sourceContent?: string;
}

export interface AIImageRequest {
  workspaceId: string;
  prompt: string;
  model?: string;
  useXai?: boolean;
}

export interface AIModel {
  id: string;
  name: string;
}

export interface MediaItem {
  id: string;
  workspace_id: string;
  r2_key: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  source: string;
  created_at: string;
}

export interface MediaListParams {
  workspaceId: string;
  type?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface UploadUrlRequest {
  workspaceId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ConfirmUploadRequest {
  workspaceId: string;
  r2Key: string;
  filename: string;
  mimeType: string;
  sizeBytes?: number;
  width?: number;
  height?: number;
  durationSeconds?: number;
}

export interface CanvaDesign {
  id: string;
  title: string;
  thumbnail?: { url: string };
}

export interface CanvaExportRequest {
  agencyId: string;
  workspaceId: string;
  designId: string;
  format: 'PNG' | 'JPG' | 'MP4' | 'PDF';
}

export interface Notification {
  id: string;
  agency_id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

export interface Invitation {
  id: string;
  agency_id?: string;
  email: string;
  role: string;
  token?: string;
  invited_by?: string;
  accepted_at: string | null;
  expires_at: string;
  created_at: string;
  inviteUrl?: string;
}

export interface InvitationLookup {
  email: string;
  role: string;
  expires_at: string;
  agency_name: string;
}

export interface InboxReply {
  id: string;
  inbox_item_id: string;
  user_id: string;
  content: string;
  platform_reply_id: string | null;
  sent_at: string;
}

export type AnalyticsStatus = 'ok' | 'insights_unavailable' | 'error' | 'rate_limited' | 'unauthorized';

export interface AnalyticsPlatformStat {
  platform: string;
  publishes: number;
  success: number;
  impressions: number;
  engagements: number;
  reach: number;
  followers: number;
  status: AnalyticsStatus;
}

export interface AnalyticsSummary {
  period: { days: number; since: string };
  posts: {
    published: number;
    byStatus: Array<{ status: string; count: number }>;
    publishSuccess: number;
    publishFailed: number;
    publishTotal: number;
  };
  platforms: AnalyticsPlatformStat[];
  dailyPosts: Array<{ date: string; count: number }>;
  accountsSynced: number;
  lastSyncedAt: string | null;
  reach: {
    impressions: number;
    engagements: number;
    reach: number;
    followers: number;
    byPlatform: AnalyticsPlatformStat[];
  };
}

export interface AnalyticsTopPost {
  id: string;
  content: string;
  published_at: string | null;
  platforms: string;
  platform_count: number;
  success_count: number;
  impressions: number;
  engagements: number;
  permalink: string | null;
  source: 'app' | 'organic';
}

// ── Admin (superadmin-only platform oversight) ────────────────────────────────

export interface AdminStats {
  totals: {
    users: number;
    superadmins: number;
    agencies: number;
    workspaces: number;
    social_accounts: number;
    posts: number;
    posts_published: number;
    posts_scheduled: number;
    posts_failed: number;
  } | null;
  postsByStatus: Array<{ status: string; count: number }>;
  accountsByPlatform: Array<{ platform: string; count: number }>;
}

export interface AdminAgencyRow {
  id: string;
  name: string;
  owner_user_id: string | null;
  owner_email: string | null;
  ai_model: string | null;
  created_at: string;
  workspace_count: number;
  member_count: number;
}

export interface AdminAgencyDetail {
  agency: Record<string, unknown> & {
    id: string;
    name: string;
    owner_email: string | null;
    owner_name: string | null;
    created_at: string;
  };
  members: Array<{
    id: string;
    role: string;
    created_at: string;
    user_id: string;
    email: string;
    name: string | null;
  }>;
  workspaces: Array<{ id: string; name: string; website: string | null; created_at: string }>;
}

export interface AdminWorkspaceRow {
  id: string;
  agency_id: string;
  name: string;
  website: string | null;
  logo_r2_key: string | null;
  created_at: string;
  agency_name: string | null;
  social_account_count: number;
  post_count: number;
  trends_enabled: number | null;
}

export interface AdminWorkspaceDetail {
  workspace: Record<string, unknown> & {
    id: string;
    agency_id: string;
    agency_name: string | null;
    name: string;
    website: string | null;
    brand_voice: string | null;
    tone: string | null;
    target_audience: string | null;
    brand_colors: string | null;
    services: string | null;
    ai_caption_guidance: string | null;
    ai_hashtag_strategy: string | null;
    created_at: string;
  };
  socialAccounts: Array<{
    id: string;
    platform: string;
    account_name: string | null;
    account_id: string | null;
    profile_image_url: string | null;
    token_expires_at: string | null;
    created_at: string;
  }>;
  postStats: Array<{ status: string; count: number }>;
  trendConfig: (Record<string, unknown> & { enabled: number }) | null;
}

export interface AdminWorkspaceUpdate {
  name?: string;
  brand_voice?: string | null;
  tone?: string | null;
  target_audience?: string | null;
  website?: string | null;
  brand_colors?: string[];
  services?: string[];
  ai_caption_guidance?: string | null;
  ai_hashtag_strategy?: string | null;
  agency_id?: string;
}

export interface AdminUserRow {
  id: string;
  email: string;
  name: string | null;
  is_superadmin: number;
  created_at: string;
  agency_count: number;
}

export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    name: string | null;
    is_superadmin: number;
    created_at: string;
    updated_at: string;
  };
  memberships: Array<{
    role: string;
    created_at: string;
    agency_id: string;
    agency_name: string;
  }>;
}

export interface AdminAuditEvent {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: string | null;
  created_at: string;
  actor_email: string | null;
  actor_name: string | null;
}

export const adminApi = {
  stats: (token: string) => request<AdminStats>('/api/admin/stats', token),

  listAgencies: (token: string, params?: { search?: string; limit?: number; offset?: number }) =>
    request<{ agencies: AdminAgencyRow[]; limit: number; offset: number }>('/api/admin/agencies', token, {
      params,
    }),
  getAgency: (token: string, id: string) =>
    request<AdminAgencyDetail>(`/api/admin/agencies/${id}`, token),
  deleteAgency: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/admin/agencies/${id}`, token, { method: 'DELETE' }),

  listWorkspaces: (
    token: string,
    params?: { search?: string; agencyId?: string; limit?: number; offset?: number }
  ) =>
    request<{ workspaces: AdminWorkspaceRow[]; total: number; limit: number; offset: number }>(
      '/api/admin/workspaces',
      token,
      { params }
    ),
  getWorkspace: (token: string, id: string) =>
    request<AdminWorkspaceDetail>(`/api/admin/workspaces/${id}`, token),
  updateWorkspace: (token: string, id: string, data: AdminWorkspaceUpdate) =>
    request<{ workspace: AdminWorkspaceDetail['workspace'] }>(`/api/admin/workspaces/${id}`, token, {
      method: 'PATCH',
      body: data,
    }),
  updateWorkspaceTrendConfig: (
    token: string,
    id: string,
    data: {
      enabled?: boolean;
      platforms?: string[];
      region?: string;
      keyword_seeds?: string[];
      hashtag_seeds?: string[];
      inject_into_ai?: boolean;
    }
  ) =>
    request<{ trendConfig: AdminWorkspaceDetail['trendConfig'] }>(
      `/api/admin/workspaces/${id}/trend-config`,
      token,
      { method: 'PUT', body: data }
    ),
  deleteWorkspace: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/admin/workspaces/${id}`, token, { method: 'DELETE' }),

  listUsers: (token: string, params?: { search?: string; limit?: number; offset?: number }) =>
    request<{ users: AdminUserRow[]; limit: number; offset: number }>('/api/admin/users', token, {
      params,
    }),
  getUser: (token: string, id: string) => request<AdminUserDetail>(`/api/admin/users/${id}`, token),
  setSuperadmin: (token: string, id: string, isSuperadmin: boolean) =>
    request<{ user: AdminUserDetail['user'] }>(`/api/admin/users/${id}`, token, {
      method: 'PATCH',
      body: { is_superadmin: isSuperadmin },
    }),

  auditLog: (token: string, params?: { limit?: number; offset?: number }) =>
    request<{ events: AdminAuditEvent[]; limit: number; offset: number }>('/api/admin/audit-log', token, {
      params,
    }),
};
